/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AuthProvider, useAuth } from './context/AuthContext';
import { PermissionProvider } from './context/PermissionContext';
import { LiveSessionProvider } from './context/LiveSessionContext';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './pages/Login';
import Templates from './pages/Templates';
import NewAssignment from './pages/NewAssignment';
import EditAssignment from './pages/EditAssignment';
import Dashboard from './pages/Dashboard';
import ReviewMedia from './pages/ReviewMedia';
import Capture from './pages/Capture';
import CaptureRedirect from './pages/CaptureRedirect';
import Layout from './components/Layout';
import Assignments from './pages/Assignments';
import Help from './pages/Help';
import ApiDocs from './pages/ApiDocs';
import Webhooks from './pages/Webhooks';
import ErrorPage from './pages/ErrorPage';
import WorkflowCreate from './pages/WorkflowCreate';
import WorkflowEdit from './pages/WorkflowEdit';
import Users from './pages/Users';
import Roles from './pages/Roles';
import WorkflowView from './pages/WorkflowView';
import Accounts from './pages/Accounts';
import NewAccount from './pages/NewAccount';
import EditAccount from './pages/EditAccount';
import SubAccounts from './pages/SubAccounts';
import NewSubAccount from './pages/NewSubAccount';
import CloneSubAccount from './pages/CloneSubAccount';
import EditSubAccount from './pages/EditSubAccount';
import NewUser from './pages/NewUser';
import NewRole from './pages/NewRole';
import EditUser from './pages/EditUser';
import EditRole from './pages/EditRole';
import Profile from './pages/Profile';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import ServiceRequests from './pages/ServiceRequests';
import NewServiceRequest from './pages/NewServiceRequest';
import EditServiceRequest from './pages/EditServiceRequest';
import ServiceRequestDetail from './pages/ServiceRequestDetail';
import ServiceRequestTypes from './pages/ServiceRequestTypes';
import NewServiceRequestType from './pages/NewServiceRequestType';
import EditServiceRequestType from './pages/EditServiceRequestType';
import MediaLibrary from './pages/MediaLibrary';
import KnowledgeBases from './pages/KnowledgeBases';
import NewKnowledgeBase from './pages/NewKnowledgeBase';
import UploadKnowledgeBaseDocument from './pages/UploadKnowledgeBaseDocument';
import KnowledgeBaseDocuments from './pages/KnowledgeBaseDocuments';
import AiAgents from './pages/AiAgents';
import AiAgentDetail from './pages/AiAgentDetail';
import EditAiAgent from './pages/EditAiAgent';
import NewAiAgent from './pages/NewAiAgent';
import PublicServiceRequest from './pages/PublicServiceRequest';


// Redirects to `redirectTo` and opens the account switcher if no sub-account is selected
const SubAccountRoute = ({ children, redirectTo = '/dashboard' }) => {
  const { user } = useAuth();
  const needsSubAccount = !user?.subAccountUID;

  useEffect(() => {
    if (needsSubAccount) {
      window.dispatchEvent(new CustomEvent('sub-account-required'));
    }
  }, [needsSubAccount]);

  if (needsSubAccount) {
    return <Navigate to={redirectTo} replace />;
  }
  return children;
};

SubAccountRoute.propTypes = {
  children: PropTypes.node.isRequired,
  redirectTo: PropTypes.string,
};

const AppRoutes = () => {
  const { user, loading } = useAuth();

  if (loading) { return null; }

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/dashboard" replace /> : <Login />} />
      <Route path="/login/:accountSlug" element={user ? <Navigate to="/dashboard" replace /> : <Login />} />
      <Route path="/forgot-password" element={user ? <Navigate to="/dashboard" replace /> : <ForgotPassword />} />
      <Route path="/reset-password" element={user ? <Navigate to="/dashboard" replace /> : <ResetPassword />} />
      <Route path="/capture/:token" element={<Capture />} />
      <Route path="/c/:shortToken" element={<CaptureRedirect />} />
      <Route path="/s/:code" element={<PublicServiceRequest />} />
      <Route path="/error" element={<ErrorPage />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />

        {/* Workflow/Template Management */}
        <Route path="workflows" element={<ProtectedRoute permissions={['templates.view']}><Templates /></ProtectedRoute>} />
        <Route path="workflow/new" element={<SubAccountRoute redirectTo="/workflows"><ProtectedRoute permissions={['templates.create']}><WorkflowCreate /></ProtectedRoute></SubAccountRoute>} />
        <Route path="workflow/:id" element={<ProtectedRoute permissions={['templates.view']}><WorkflowView /></ProtectedRoute>} />
        <Route path="workflow/:id/edit" element={<SubAccountRoute redirectTo="/workflows"><ProtectedRoute permissions={['templates.update']}><WorkflowEdit /></ProtectedRoute></SubAccountRoute>} />

        {/* Assignment Management */}
        <Route path="assignments" element={<ProtectedRoute permissions={['assignments.view']}><Assignments /></ProtectedRoute>} />
        <Route path="assignments/new" element={<SubAccountRoute redirectTo="/assignments"><ProtectedRoute permissions={['assignments.create']}><NewAssignment /></ProtectedRoute></SubAccountRoute>} />
        <Route path="assignments/:id" element={<ProtectedRoute permissions={['assignments.view_media']}><ReviewMedia /></ProtectedRoute>} />
        <Route path="assignments/:id/edit" element={<SubAccountRoute redirectTo="/assignments"><ProtectedRoute permissions={['assignments.update']}><EditAssignment /></ProtectedRoute></SubAccountRoute>} />

        {/* User Management */}
        <Route path="users" element={<ProtectedRoute permissions={['users.view']}><Users /></ProtectedRoute>} />
        <Route path="users/new" element={<ProtectedRoute permissions={['users.create']}><NewUser /></ProtectedRoute>} />
        <Route path="users/:id/edit" element={<ProtectedRoute permissions={['users.update']}><EditUser /></ProtectedRoute>} />

        {/* Role Management */}
        <Route path="roles" element={<ProtectedRoute permissions={['roles.view']}><Roles /></ProtectedRoute>} />
        <Route path="roles/new" element={<ProtectedRoute permissions={['roles.create']}><NewRole /></ProtectedRoute>} />
        <Route path="roles/:id/edit" element={<ProtectedRoute permissions={['roles.update']}><EditRole /></ProtectedRoute>} />

        {/* Account Management */}
        <Route path="accounts" element={<ProtectedRoute permissions={['accounts.view']} excludeScopes={['account', 'sub_account']}><Accounts /></ProtectedRoute>} />
        <Route path="accounts/new" element={<ProtectedRoute permissions={['accounts.create']} excludeScopes={['account', 'sub_account']}><NewAccount /></ProtectedRoute>} />
        <Route path="accounts/:id/edit" element={<ProtectedRoute permissions={['accounts.update']} excludeScopes={['account', 'sub_account']}><EditAccount /></ProtectedRoute>} />

        {/* Sub Account Management */}
        <Route path="sub-accounts" element={<ProtectedRoute permissions={['sub_accounts.view']} excludeScopes={['sub_account']}><SubAccounts /></ProtectedRoute>} />
        <Route path="sub-accounts/new" element={<ProtectedRoute permissions={['sub_accounts.create']} excludeScopes={['sub_account']}><NewSubAccount /></ProtectedRoute>} />
        <Route path="sub-accounts/:id/clone" element={<ProtectedRoute permissions={['sub_accounts.create']} excludeScopes={['sub_account']}><CloneSubAccount /></ProtectedRoute>} />
        <Route path="sub-accounts/:id/edit" element={<ProtectedRoute permissions={['sub_accounts.update']} excludeScopes={['sub_account']}><EditSubAccount /></ProtectedRoute>} />

        {/* Service Request Management */}
        <Route path="service-requests" element={<ProtectedRoute permissions={['service_requests.view']}><ServiceRequests /></ProtectedRoute>} />
        <Route path="service-requests/new" element={<ProtectedRoute permissions={['service_requests.create']}><NewServiceRequest /></ProtectedRoute>} />
        <Route path="service-requests/:id" element={<ProtectedRoute permissions={['service_requests.view']}><ServiceRequestDetail /></ProtectedRoute>} />
        <Route path="service-requests/:id/edit" element={<ProtectedRoute permissions={['service_requests.edit']}><EditServiceRequest /></ProtectedRoute>} />
        <Route path="service-requests/types" element={<ProtectedRoute permissions={['service_requests.create']} platformOnly><ServiceRequestTypes /></ProtectedRoute>} />
        <Route path="service-requests/types/new" element={<ProtectedRoute permissions={['service_requests.create']} platformOnly><NewServiceRequestType /></ProtectedRoute>} />
        <Route path="service-requests/types/:id/edit" element={<ProtectedRoute permissions={['service_requests.create']} platformOnly><EditServiceRequestType /></ProtectedRoute>} />

        {/* Media Library */}
        <Route path="media-library" element={<ProtectedRoute permissions={['service_requests.view']}><MediaLibrary /></ProtectedRoute>} />

        {/* AI Agent — Knowledge Base (no permission keys yet; any signed-in user) */}
        <Route path="knowledge-bases" element={<ProtectedRoute permissions={['knowledge_bases.view']}><KnowledgeBases /></ProtectedRoute>} />
        <Route path="knowledge-bases/new" element={<ProtectedRoute permissions={['knowledge_bases.create']}><NewKnowledgeBase /></ProtectedRoute>} />
        <Route path="knowledge-bases/:id/documents" element={<ProtectedRoute permissions={['knowledge_bases.view']}><KnowledgeBaseDocuments /></ProtectedRoute>} />
        <Route path="knowledge-bases/:id/documents/new" element={<ProtectedRoute permissions={['knowledge_bases.upload']}><UploadKnowledgeBaseDocument /></ProtectedRoute>} />
        <Route path="ai-agents" element={<ProtectedRoute permissions={['ai_agents.view']}><AiAgents /></ProtectedRoute>} />
        <Route path="ai-agents/new" element={<ProtectedRoute permissions={['ai_agents.create']}><NewAiAgent /></ProtectedRoute>} />
        <Route path="ai-agents/:id" element={<ProtectedRoute permissions={['ai_agents.view']}><AiAgentDetail /></ProtectedRoute>} />
        <Route path="ai-agents/:id/edit" element={<ProtectedRoute permissions={['ai_agents.update']}><EditAiAgent /></ProtectedRoute>} />

        <Route path="webhooks" element={<ProtectedRoute permissions={['webhooks.view']}><Webhooks /></ProtectedRoute>} />
        <Route path="appearance" element={<Navigate to="/profile" replace />} />
        <Route path="settings" element={<Navigate to="/profile" replace />} />
        <Route path="settings/appearance" element={<Navigate to="/profile" replace />} />
        <Route path="help" element={<Help />} />
        <Route path="api-docs" element={<ApiDocs />} />
        <Route path="profile" element={<Profile />} />

      </Route>
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};

const App = () => {
  return (
    <BrowserRouter>
      <AuthProvider>
        <PermissionProvider>
          <LiveSessionProvider>
            <AppRoutes />
          </LiveSessionProvider>
        </PermissionProvider>
        <ToastContainer />
      </AuthProvider>
    </BrowserRouter>
  );
};

export default App;
